import React, { useEffect, useState, useCallback } from 'react';
import Button from '../../../components/common/Button';
import GradientButton from '../../../components/common/GradientButton';
import Icon from '../../../components/common/Icon';
import Input from '../../../components/common/Input';
import Modal from '../../../components/common/Modal';
import Card from '../../../components/common/Card';
import JalaliDatePicker from '../../../components/common/JalaliDatePicker';
import {
  getStagesApi,
  getStageApi,
  createStageApi,
  deleteStageApi,
  generateKnockoutApi,
} from '../../../api/stage.api';
import {
  createGroupApi,
  deleteGroupApi,
  getGroupStandingsApi,
  assignRegistrationToGroupApi,
  removeRegistrationFromGroupApi,
} from '../../../api/group.api';
import { getTournamentApi } from '../../../api/tournament.api';
import {
  STAGE_TYPE_LABELS,
  MATCH_TYPE_LABELS,
  GENDER_LABELS,
  AGE_GROUP_LABELS,
} from '../../../utils/constants';
import { toJalali } from '../../../utils/jalali';

const emptyStageForm = { name: '', type: 'GROUP', order: 1 };
const emptyGroupForm = { name: '' };
const emptyKnockoutForm = {
  sourceStageId: '',
  qualifiersPerGroup: 1,
  matchDefaults: {
    type: 'FOOTBALL',
    gender: 'MALE',
    ageGroup: '',
    date: '',
    location: '',
    season: '',
  },
};

function groupMatchesByRound(matches) {
  const rounds = {};
  (matches || []).forEach((m) => {
    const r = m.bracketRound || 1;
    if (!rounds[r]) rounds[r] = [];
    rounds[r].push(m);
  });
  return Object.entries(rounds)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([round, ms]) => ({
      round: Number(round),
      matches: [...ms].sort((a, b) => (a.bracketSlot ?? 0) - (b.bracketSlot ?? 0)),
    }));
}

function BracketView({ matches }) {
  const rounds = groupMatchesByRound(matches);
  if (rounds.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-slate-400">
        هنوز مسابقه‌ای برای این مرحله ساخته نشده است
      </div>
    );
  }
  return (
    <div className="flex gap-6 overflow-x-auto pb-2">
      {rounds.map(({ round, matches: ms }) => (
        <div key={round} className="flex min-w-[220px] flex-col justify-around gap-4">
          <div className="mb-1 text-center text-xs font-bold text-emerald-600">
            {ms.length === 1 ? 'فینال' : `دور ${round}`}
          </div>
          {ms.map((m) => (
            <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="flex items-center justify-between text-sm">
                <span
                  className={
                    m.result?.winnerClubId === m.homeClubId ? 'font-bold text-emerald-600' : ''
                  }
                >
                  {m.homeClub?.name}
                </span>
                <span className="font-mono text-xs">{m.result?.homeScore ?? '-'}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-sm">
                <span
                  className={
                    m.result?.winnerClubId === m.awayClubId ? 'font-bold text-emerald-600' : ''
                  }
                >
                  {m.awayClub?.name}
                </span>
                <span className="font-mono text-xs">{m.result?.awayScore ?? '-'}</span>
              </div>
              <div className="mt-2 text-[10px] text-slate-400">{toJalali(m.date)}</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function StagesManager({ tournamentId, tournamentName }) {
  const [stages, setStages] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [error, setError] = useState('');

  const [stageFormOpen, setStageFormOpen] = useState(false);
  const [stageForm, setStageForm] = useState(emptyStageForm);

  const [groupFormStageId, setGroupFormStageId] = useState(null);
  const [groupForm, setGroupForm] = useState(emptyGroupForm);

  const [standingsByGroup, setStandingsByGroup] = useState({});

  const [assignDialog, setAssignDialog] = useState(null); // { stageId, groupId }
  const [selectedRegistration, setSelectedRegistration] = useState('');

  const [knockoutDialogStageId, setKnockoutDialogStageId] = useState(null);
  const [knockoutForm, setKnockoutForm] = useState(emptyKnockoutForm);

  const [detailStageId, setDetailStageId] = useState(null);
  const [stageDetail, setStageDetail] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: stagesRes }, { data: tourRes }] = await Promise.all([
        getStagesApi(tournamentId),
        getTournamentApi(tournamentId),
      ]);
      setStages(stagesRes.data);
      setRegistrations(tourRes.data.registrations || []);
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در دریافت اطلاعات مراحل');
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadStageDetail = async (stageId) => {
    try {
      const { data } = await getStageApi(stageId);
      setStageDetail((prev) => ({ ...prev, [stageId]: data.data }));
      (data.data.groups || []).forEach((g) => fetchGroupStandings(g.id));
    } catch (err) {
      setSnackbar(err.response?.data?.message);
    }
  };

  const toggleDetail = (stageId) => {
    if (detailStageId === stageId) {
      setDetailStageId(null);
      return;
    }
    setDetailStageId(stageId);
    loadStageDetail(stageId);
  };

  const fetchGroupStandings = async (groupId) => {
    try {
      const { data } = await getGroupStandingsApi(groupId);
      setStandingsByGroup((prev) => ({ ...prev, [groupId]: data.data }));
    } catch {
      /* ignore */
    }
  };

  // ----- Stage CRUD -----
  const handleCreateStage = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createStageApi({ ...stageForm, tournamentId, order: Number(stageForm.order) });
      setSnackbar('مرحله با موفقیت ایجاد شد');
      setStageFormOpen(false);
      setStageForm({ ...emptyStageForm, order: stages.length + 1 });
      load();
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  const handleDeleteStage = async (stageId) => {
    if (!window.confirm('این مرحله و همه‌ی گروه‌ها/مسابقات آن حذف می‌شود. ادامه می‌دهید؟')) return;
    try {
      await deleteStageApi(stageId);
      setSnackbar('مرحله حذف شد');
      if (detailStageId === stageId) setDetailStageId(null);
      load();
    } catch (err) {
      setSnackbar(err.response?.data?.message);
    }
  };

  // ----- Group CRUD -----
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createGroupApi({ stageId: groupFormStageId, name: groupForm.name });
      setSnackbar('گروه با موفقیت ایجاد شد');
      const stageId = groupFormStageId;
      setGroupFormStageId(null);
      setGroupForm(emptyGroupForm);
      loadStageDetail(stageId);
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  const handleDeleteGroup = async (stageId, groupId) => {
    if (!window.confirm('این گروه حذف می‌ شود. ادامه می‌ دهید؟')) return;
    try {
      await deleteGroupApi(groupId);
      setSnackbar('گروه حذف شد');
      loadStageDetail(stageId);
      load();
    } catch (err) {
      setSnackbar(err.response?.data?.message);
    }
  };

  // ----- Assign / remove club <-> group -----
  // توجه: هر ثبت‌نام (Registration) فقط می‌تواند در یک گروه در کل تورنمنت عضو باشد
  const availableRegistrations = registrations.filter(
    (r) => r.status === 'APPROVED' && !r.groupId
  );

  const handleAssign = async () => {
    if (!assignDialog || !selectedRegistration) return;
    try {
      await assignRegistrationToGroupApi(assignDialog.groupId, Number(selectedRegistration));
      setSnackbar('باشگاه به گروه اضافه شد');
      const { stageId } = assignDialog;
      setAssignDialog(null);
      setSelectedRegistration('');
      loadStageDetail(stageId);
      load();
    } catch (err) {
      setSnackbar(err.response?.data?.message);
    }
  };

  const handleRemoveFromGroup = async (stageId, groupId, registrationId) => {
    try {
      await removeRegistrationFromGroupApi(groupId, registrationId);
      setSnackbar('باشگاه از گروه حذف شد');
      loadStageDetail(stageId);
      load();
    } catch (err) {
      setSnackbar(err.response?.data?.message);
    }
  };

  // ----- Knockout generation -----
  const groupStages = stages.filter((s) => s.type === 'GROUP');

  const handleGenerateKnockout = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        sourceStageId: Number(knockoutForm.sourceStageId),
        qualifiersPerGroup: Number(knockoutForm.qualifiersPerGroup),
        matchDefaults: {
          ...knockoutForm.matchDefaults,
          ageGroup: knockoutForm.matchDefaults.ageGroup || null,
        },
      };
      await generateKnockoutApi(knockoutDialogStageId, payload);
      setSnackbar('جدول حذفی با موفقیت ساخته شد');
      const stageId = knockoutDialogStageId;
      setKnockoutDialogStageId(null);
      setKnockoutForm(emptyKnockoutForm);
      loadStageDetail(stageId);
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-text-dark">مراحل و گروه‌های تورنمنت</h3>
          <p className="text-xs text-slate-400">{tournamentName}</p>
        </div>
        <GradientButton
          size="sm"
          onClick={() => {
            setStageForm({ ...emptyStageForm, order: stages.length + 1 });
            setStageFormOpen(true);
          }}
        >
          <Icon name="plus" className="ml-2 h-4 w-4" />
          مرحله جدید
        </GradientButton>
      </div>

      {snackbar && (
        <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">
          {snackbar}
          <button className="mr-2" onClick={() => setSnackbar('')}>✕</button>
        </div>
      )}

      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
        </div>
      ) : (
        <div className="space-y-3">
          {stages.map((stage) => {
            const detail = stageDetail[stage.id];
            return (
              <Card key={stage.id} className="p-0 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3">
                  <button
                    onClick={() => toggleDetail(stage.id)}
                    className="flex items-center gap-3 text-right"
                  >
                    <Icon
                      name="chevronDown"
                      className={`h-4 w-4 transition-transform duration-300 ${
                        detailStageId === stage.id ? 'rotate-180' : ''
                      }`}
                    />
                    <div>
                      <span className="font-bold text-text-dark">{stage.name}</span>
                      <span className="mr-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                        {STAGE_TYPE_LABELS[stage.type] || stage.type}
                      </span>
                      <span className="mr-2 text-[11px] text-slate-400">ترتیب: {stage.order}</span>
                    </div>
                  </button>
                  <button
                    onClick={() => handleDeleteStage(stage.id)}
                    className="grid h-8 w-8 place-items-center rounded-xl text-red-500 transition hover:scale-110 hover:bg-red-100"
                    title="حذف مرحله"
                  >
                    <Icon name="trash" className="h-3.5 w-3.5" />
                  </button>
                </div>

                {detailStageId === stage.id && (
                  <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-4">
                    {!detail ? (
                      <div className="py-4 text-center text-sm text-slate-400">در حال بارگذاری...</div>
                    ) : stage.type === 'GROUP' ? (
                      <div className="space-y-4">
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setGroupForm(emptyGroupForm);
                              setGroupFormStageId(stage.id);
                            }}
                          >
                            <Icon name="plus" className="ml-1 h-3.5 w-3.5" />
                            گروه جدید
                          </Button>
                        </div>

                        {(detail.groups || []).length === 0 && (
                          <div className="py-3 text-center text-sm text-slate-400">
                            هنوز گروهی برای این مرحله ساخته نشده است
                          </div>
                        )}

                        {(detail.groups || []).map((group) => (
                          <div key={group.id} className="rounded-xl border border-slate-200 bg-white p-4">
                            <div className="mb-3 flex items-center justify-between">
                              <h4 className="font-bold text-text-dark">{group.name}</h4>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedRegistration('');
                                    setAssignDialog({ stageId: stage.id, groupId: group.id });
                                  }}
                                >
                                  <Icon name="users" className="ml-1 h-3.5 w-3.5" />
                                  افزودن باشگاه
                                </Button>
                                <button
                                  onClick={() => handleDeleteGroup(stage.id, group.id)}
                                  className="grid h-7 w-7 place-items-center rounded-lg text-red-500 hover:bg-red-100"
                                  title="حذف گروه"
                                >
                                  <Icon name="trash" className="h-3 w-3" />
                                </button>
                              </div>
                            </div>

                            <div className="overflow-x-auto">
                              <table className="w-full text-right text-xs">
                                <thead>
                                  <tr className="border-b border-slate-200 text-slate-500">
                                    <th className="px-2 py-1.5">باشگاه</th>
                                    <th className="px-2 py-1.5 text-center">بازی</th>
                                    <th className="px-2 py-1.5 text-center">برد</th>
                                    <th className="px-2 py-1.5 text-center">مساوی</th>
                                    <th className="px-2 py-1.5 text-center">باخت</th>
                                    <th className="px-2 py-1.5 text-center">تفاضل</th>
                                    <th className="px-2 py-1.5 text-center">امتیاز</th>
                                    <th className="px-2 py-1.5 text-center"></th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {(standingsByGroup[group.id] || []).map((row, idx) => {
                                    const reg = group.registrations.find(
                                      (r) => r.club.id === row.club.id
                                    );
                                    return (
                                      <tr key={row.club.id}>
                                        <td className="px-2 py-1.5 font-medium">
                                          {idx + 1}. {row.club.name}
                                        </td>
                                        <td className="px-2 py-1.5 text-center">{row.played}</td>
                                        <td className="px-2 py-1.5 text-center text-emerald-600">
                                          {row.won}
                                        </td>
                                        <td className="px-2 py-1.5 text-center text-amber-600">
                                          {row.drawn}
                                        </td>
                                        <td className="px-2 py-1.5 text-center text-red-500">
                                          {row.lost}
                                        </td>
                                        <td className="px-2 py-1.5 text-center font-mono">
                                          {row.goalDiff}
                                        </td>
                                        <td className="px-2 py-1.5 text-center font-bold text-emerald-600">
                                          {row.points}
                                        </td>
                                        <td className="px-2 py-1.5 text-center">
                                          {reg && (
                                            <button
                                              onClick={() =>
                                                handleRemoveFromGroup(stage.id, group.id, reg.id)
                                              }
                                              className="text-red-500 hover:text-red-700"
                                              title="حذف از گروه"
                                            >
                                              <Icon name="close" className="h-3 w-3" />
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {(group.registrations || []).length === 0 && (
                                    <tr>
                                      <td colSpan="8" className="py-3 text-center text-slate-400">
                                        هنوز باشگاهی به این گروه اضافه نشده است
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {(!detail.matches || detail.matches.length === 0) && (
                          <div className="flex justify-end">
                            <GradientButton
                              size="sm"
                              onClick={() => {
                                setKnockoutForm(emptyKnockoutForm);
                                setKnockoutDialogStageId(stage.id);
                              }}
                            >
                              <Icon name="trophy" className="ml-2 h-4 w-4" />
                              ساخت جدول حذفی از روی گروه‌ها
                            </GradientButton>
                          </div>
                        )}
                        <BracketView matches={detail.matches} />
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}

          {stages.length === 0 && (
            <div className="py-8 text-center text-slate-400">هنوز مرحله‌ای ساخته نشده است</div>
          )}
        </div>
      )}

      {/* مودال ایجاد مرحله */}
      <Modal open={stageFormOpen} onClose={() => setStageFormOpen(false)} title="ایجاد مرحله جدید">
        <form onSubmit={handleCreateStage} className="space-y-4">
          {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}
          <Input
            label="نام مرحله (مثلا: مرحله گروهی، یک‌ چهارم نهایی)"
            value={stageForm.name}
            onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })}
            required
          />
          <div>
            <label className="mb-2 block text-sm font-medium text-text-dark/80">نوع مرحله</label>
            <select
              className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
              value={stageForm.type}
              onChange={(e) => setStageForm({ ...stageForm, type: e.target.value })}
            >
              {Object.entries(STAGE_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="ترتیب اجرا"
            type="number"
            min="1"
            value={stageForm.order}
            onChange={(e) => setStageForm({ ...stageForm, order: e.target.value })}
            required
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" type="button" onClick={() => setStageFormOpen(false)}>
              انصراف
            </Button>
            <GradientButton type="submit">ایجاد مرحله</GradientButton>
          </div>
        </form>
      </Modal>

      {/* مودال ایجاد گروه */}
      <Modal open={Boolean(groupFormStageId)} onClose={() => setGroupFormStageId(null)} title="ایجاد گروه جدید">
        <form onSubmit={handleCreateGroup} className="space-y-4">
          {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}
          <Input
            label="نام گروه (مثلا: گروه A)"
            value={groupForm.name}
            onChange={(e) => setGroupForm({ name: e.target.value })}
            required
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" type="button" onClick={() => setGroupFormStageId(null)}>
              انصراف
            </Button>
            <GradientButton type="submit">ایجاد گروه</GradientButton>
          </div>
        </form>
      </Modal>

      {/* مودال افزودن باشگاه به گروه */}
      <Modal open={Boolean(assignDialog)} onClose={() => setAssignDialog(null)} title="افزودن باشگاه به گروه">
        <div className="space-y-4">
          <p className="text-xs text-slate-400">
            فقط باشگاه‌های تاییدشده‌ی این تورنمنت که هنوز عضو هیچ گروهی نیستند نمایش داده می‌ شوند.
          </p>
          <select
            className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
            value={selectedRegistration}
            onChange={(e) => setSelectedRegistration(e.target.value)}
          >
            <option value="">انتخاب باشگاه</option>
            {availableRegistrations.map((r) => (
              <option key={r.id} value={r.id}>
                {r.club?.name}
              </option>
            ))}
          </select>
          {availableRegistrations.length === 0 && (
            <p className="text-xs text-amber-600">
              باشگاه تاییدشده‌ی بدون گروهی وجود ندارد.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setAssignDialog(null)}>
              انصراف
            </Button>
            <GradientButton onClick={handleAssign} disabled={!selectedRegistration}>
              افزودن
            </GradientButton>
          </div>
        </div>
      </Modal>

      {/* مودال ساخت جدول حذفی */}
      <Modal
        open={Boolean(knockoutDialogStageId)}
        onClose={() => setKnockoutDialogStageId(null)}
        title="ساخت جدول حذفی از روی مرحله‌ی گروهی"
        size="lg"
      >
        <form onSubmit={handleGenerateKnockout} className="space-y-4">
          {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}
          <div>
            <label className="mb-2 block text-sm font-medium text-text-dark/80">
              مرحله‌ی گروهی مبدا <span className="mr-1 font-black text-red-500" aria-hidden="true">*</span>
            </label>
            <select
              className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
              value={knockoutForm.sourceStageId}
              onChange={(e) => setKnockoutForm({ ...knockoutForm, sourceStageId: e.target.value })}
              required
            >
              <option value="">انتخاب مرحله</option>
              {groupStages.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="تعداد تیم های صعودی از هر گروه"
            type="number"
            min="1"
            max="8"
            value={knockoutForm.qualifiersPerGroup}
            onChange={(e) =>
              setKnockoutForm({ ...knockoutForm, qualifiersPerGroup: e.target.value })
            }
            required
          />
          <p className="text-[11px] text-slate-400">
            تعداد کل صعودکنندگان (تعداد گروه‌ها × تیم های صعودی) باید توان ۲ باشد (مثلا ۲، ۴، ۸، ۱۶).
          </p>

          <div className="border-t border-slate-100 pt-3">
            <p className="mb-3 text-sm font-bold text-text-dark">تنظیمات پیش فرض مسابقات دور اول</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-text-dark/80">نوع مسابقه</label>
                <select
                  className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
                  value={knockoutForm.matchDefaults.type}
                  onChange={(e) =>
                    setKnockoutForm({
                      ...knockoutForm,
                      matchDefaults: { ...knockoutForm.matchDefaults, type: e.target.value },
                    })
                  }
                >
                  {Object.entries(MATCH_TYPE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-text-dark/80">جنسیت</label>
                <select
                  className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
                  value={knockoutForm.matchDefaults.gender}
                  onChange={(e) =>
                    setKnockoutForm({
                      ...knockoutForm,
                      matchDefaults: { ...knockoutForm.matchDefaults, gender: e.target.value },
                    })
                  }
                >
                  {Object.entries(GENDER_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-text-dark/80">
                  رده سنی (اختیاری)
                </label>
                <select
                  className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
                  value={knockoutForm.matchDefaults.ageGroup}
                  onChange={(e) =>
                    setKnockoutForm({
                      ...knockoutForm,
                      matchDefaults: { ...knockoutForm.matchDefaults, ageGroup: e.target.value },
                    })
                  }
                >
                  <option value="">بدون رده سنی</option>
                  {Object.entries(AGE_GROUP_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <JalaliDatePicker
                label="تاریخ مسابقات دور اول"
                value={knockoutForm.matchDefaults.date}
                onChange={(date) =>
                  setKnockoutForm({
                    ...knockoutForm,
                    matchDefaults: { ...knockoutForm.matchDefaults, date },
                  })
                }
                required
              />
              <Input
                label="محل برگزاری"
                value={knockoutForm.matchDefaults.location}
                onChange={(e) =>
                  setKnockoutForm({
                    ...knockoutForm,
                    matchDefaults: { ...knockoutForm.matchDefaults, location: e.target.value },
                  })
                }
                required
              />
              <Input
                label="فصل مسابقات (مثلا 1404-1405)"
                value={knockoutForm.matchDefaults.season}
                onChange={(e) =>
                  setKnockoutForm({
                    ...knockoutForm,
                    matchDefaults: { ...knockoutForm.matchDefaults, season: e.target.value },
                  })
                }
                required
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" type="button" onClick={() => setKnockoutDialogStageId(null)}>
              انصراف
            </Button>
            <GradientButton type="submit">ساخت جدول حذفی</GradientButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}